function f(path, label, type, opts) {
    return {
        path,
        label,
        type,
        required: opts?.required ?? false,
        group: opts?.group,
    };
}
/** Catalog of fields users can map CSV columns onto (Company). */
export const COMPANY_MAPPABLE_FIELDS = [
    f("company_id", "Company ID", "string", { group: "Identity" }),
    f("company_name", "Company name", "string", { required: true, group: "Identity" }),
    f("company_name_for_emails", "Company name for emails", "string", { group: "Identity" }),
    f("website", "Website", "string", { group: "Identity" }),
    f("industry", "Industry", "string", { group: "Commercial" }),
    f("num_employees", "Employee count", "number", { group: "Commercial" }),
    f("account_stage", "Account stage", "string", { group: "Commercial" }),
    f("account_owner", "Account owner", "string", { group: "Commercial" }),
    f("company_phone", "Company phone", "string", { group: "Commercial" }),
    f("keywords", "Keywords", "string_list", { group: "Commercial" }),
    f("technologies", "Technologies", "string_list", { group: "Commercial" }),
    f("address.street", "Street", "string", { group: "Address" }),
    f("address.city", "City", "string", { group: "Address" }),
    f("address.state", "State", "string", { group: "Address" }),
    f("address.country", "Country", "string", { group: "Address" }),
    f("address.postal_code", "Postal code", "string", { group: "Address" }),
    f("address.full_address", "Full address", "string", { group: "Address" }),
    f("social_urls.linkedin", "LinkedIn URL", "string", { group: "Social" }),
    f("social_urls.facebook", "Facebook URL", "string", { group: "Social" }),
    f("social_urls.twitter", "Twitter URL", "string", { group: "Social" }),
    f("company_details.short_description", "Description", "string", { group: "Details" }),
    f("company_details.founded_year", "Founded year", "number", { group: "Details" }),
    f("company_details.logo_url", "Logo URL", "string", { group: "Details" }),
    f("company_details.subsidiary_of", "Subsidiary of", "string", { group: "Details" }),
    f("financial_data.annual_revenue", "Annual revenue", "number", { group: "Financial" }),
    f("financial_data.total_funding", "Total funding", "number", { group: "Financial" }),
    f("financial_data.latest_funding", "Latest funding", "string", { group: "Financial" }),
    f("financial_data.latest_funding_amount", "Latest funding amount", "number", {
        group: "Financial",
    }),
    f("financial_data.last_raised_at", "Last raised at", "string", { group: "Financial" }),
    f("external_ids.apollo_account_id", "Apollo account ID", "string", { group: "External IDs" }),
    f("external_ids.apollo_record_id", "Apollo record ID", "string", { group: "External IDs" }),
    f("external_ids.sic_codes", "SIC codes", "string_list", { group: "External IDs" }),
    f("external_ids.naics_codes", "NAICS codes", "string_list", { group: "External IDs" }),
    f("qualification_data.account_notes", "Account notes", "string", { group: "Qualification" }),
    f("data_source", "Data source", "string", { group: "Provenance" }),
];
/** Catalog of fields users can map CSV columns onto (Person). */
export const PERSON_MAPPABLE_FIELDS = [
    f("person_id", "Person ID", "string", { group: "Identity" }),
    f("first_name", "First name", "string", { group: "Identity" }),
    f("last_name", "Last name", "string", { group: "Identity" }),
    f("name", "Full name", "string", { group: "Identity" }),
    f("title", "Title", "string", { group: "Identity" }),
    f("seniority", "Seniority", "string", { group: "Identity" }),
    f("departments", "Departments", "string_list", { group: "Identity" }),
    f("sub_departments", "Sub-departments", "string_list", { group: "Identity" }),
    f("person_owner", "Person owner", "string", { group: "Identity" }),
    f("email_data.primary_email", "Primary email", "string", { group: "Email" }),
    f("email_data.primary_email_status", "Email status", "string", { group: "Email" }),
    f("email_data.primary_email_source", "Email source", "string", { group: "Email" }),
    f("email_data.secondary_email", "Secondary email", "string", { group: "Email" }),
    f("email_data.tertiary_email", "Tertiary email", "string", { group: "Email" }),
    f("phone_data.work_direct_phone", "Work phone", "string", { group: "Phone" }),
    f("phone_data.mobile_phone", "Mobile phone", "string", { group: "Phone" }),
    f("phone_data.corporate_phone", "Corporate phone", "string", { group: "Phone" }),
    f("phone_data.other_phone", "Other phone", "string", { group: "Phone" }),
    f("phone_data.do_not_call", "Do not call", "boolean", { group: "Phone" }),
    f("company_data.company_id", "Company ID", "string", { group: "Company" }),
    f("company_data.company_name", "Company name", "string", { group: "Company" }),
    f("company_data.industry", "Company industry", "string", { group: "Company" }),
    f("company_data.num_employees", "Company employees", "number", { group: "Company" }),
    f("company_data.website", "Company website", "string", { group: "Company" }),
    f("company_data.account_owner", "Account owner", "string", { group: "Company" }),
    f("company_data.keywords", "Company keywords", "string_list", { group: "Company" }),
    f("company_data.technologies", "Company technologies", "string_list", { group: "Company" }),
    f("company_data.annual_revenue", "Company revenue", "number", { group: "Company" }),
    f("company_data.sic_codes", "Company SIC codes", "string_list", { group: "Company" }),
    f("company_data.naics_codes", "Company NAICS codes", "string_list", { group: "Company" }),
    f("social_urls.person_linkedin_url", "Person LinkedIn", "string", { group: "Social" }),
    f("social_urls.company_linkedin_url", "Company LinkedIn", "string", { group: "Social" }),
    f("social_urls.website", "Website", "string", { group: "Social" }),
    f("social_urls.facebook_url", "Facebook", "string", { group: "Social" }),
    f("social_urls.twitter_url", "Twitter", "string", { group: "Social" }),
    f("location_data.city", "City", "string", { group: "Location" }),
    f("location_data.state", "State", "string", { group: "Location" }),
    f("location_data.country", "Country", "string", { group: "Location" }),
    f("location_data.company_address", "Company address", "string", { group: "Location" }),
    f("location_data.company_city", "Company city", "string", { group: "Location" }),
    f("location_data.company_state", "Company state", "string", { group: "Location" }),
    f("location_data.company_country", "Company country", "string", { group: "Location" }),
    f("location_data.company_phone", "Company phone", "string", { group: "Location" }),
    f("sales_activity.stage", "Stage", "string", { group: "Sales" }),
    f("sales_activity.lists", "Lists", "string_list", { group: "Sales" }),
    f("sales_activity.last_contacted", "Last contacted", "string", { group: "Sales" }),
    f("external_ids.apollo_person_id", "Apollo person ID", "string", { group: "External IDs" }),
    f("external_ids.apollo_account_id", "Apollo account ID", "string", { group: "External IDs" }),
    f("external_ids.apollo_record_id", "Apollo record ID", "string", { group: "External IDs" }),
    f("data_source", "Data source", "string", { group: "Provenance" }),
];
export function getMappableFields(entityType) {
    return entityType === "company" ? COMPANY_MAPPABLE_FIELDS : PERSON_MAPPABLE_FIELDS;
}
export function getMappableFieldMap(entityType) {
    return new Map(getMappableFields(entityType).map((x) => [x.path, x]));
}
//# sourceMappingURL=mappable-fields.js.map